import { DOMParser, DOMSerializer } from "@tiptap/pm/model"
import { CellSelection } from "@tiptap/pm/tables"
import { Transform } from "@tiptap/pm/transform"

import type { Node as ProseMirrorNode, ResolvedPos, Schema, Slice } from "@tiptap/pm/model"
import type { EditorState, Selection, Transaction } from "@tiptap/pm/state"
import type { Editor } from "@tiptap/react"

import { markdownToTiptapHtml, markdownToTiptapHtmlInline } from "../tiptap/markdown-to-tiptap-html"

export type EditRange = {
  from: number
  to: number
}

/**
 * The part of the document an AI action works on. `cellRanges` is set for a
 * table cell selection; with more than one cell each cell is edited on its own.
 */
export type SelectionEditTarget = EditRange & {
  cellRanges?: EditRange[]
}

export function findCellDepth(pos: {
  depth: number
  node: (depth: number) => { type: { name: string } }
}): number {
  for (let depth = pos.depth; depth > 0; depth--) {
    const name = pos.node(depth).type.name
    if (name === "tableCell" || name === "tableHeader") return depth
  }
  return -1
}

/**
 * The selection an AI action should be scoped to, or null when it should work
 * on the whole document: nothing selected, or everything selected (select-all).
 */
export function getSelectionEditTarget(selection: Selection): SelectionEditTarget | null {
  if (selection instanceof CellSelection) {
    // Document order: cell values are matched to cells by index.
    const cellRanges = selection.ranges
      .map((range) => ({ from: range.$from.pos, to: range.$to.pos }))
      .sort((a, b) => a.from - b.from)
    if (cellRanges.length === 0) return null
    return {
      from: Math.min(...cellRanges.map((range) => range.from)),
      to: Math.max(...cellRanges.map((range) => range.to)),
      cellRanges,
    }
  }

  const { from, to } = selection
  if (from === to) return null
  if (from <= 0 && to >= selection.$from.doc.content.size) return null
  return { from, to }
}

function cellStart(pos: ResolvedPos): number | null {
  const depth = findCellDepth(pos)
  return depth === -1 ? null : pos.start(depth)
}

/**
 * True when a text selection starts and ends in different table cells, or
 * enters a table from outside it. Replacing such a range would tear the table.
 */
export function selectionCrossesTableCells(
  doc: ProseMirrorNode,
  from: number,
  to: number
): boolean {
  return cellStart(doc.resolve(from)) !== cellStart(doc.resolve(to))
}

export function isMultiCellTarget(target: SelectionEditTarget): boolean {
  return Boolean(target.cellRanges && target.cellRanges.length > 1)
}

export function serializeRangeToHtml(doc: ProseMirrorNode, range: EditRange): string {
  const fragment = DOMSerializer.fromSchema(doc.type.schema).serializeFragment(
    doc.slice(range.from, range.to).content
  )
  const container = document.createElement("div")
  container.appendChild(fragment)
  return container.innerHTML
}

export function parseHtmlToSlice(schema: Schema, html: string): Slice {
  const container = document.createElement("div")
  container.innerHTML = html
  return DOMParser.fromSchema(schema).parseSlice(container)
}

export function normalizeInsertedMarkdown(markdown: string): string {
  return markdown.replace(/\r\n/g, "\n").replace(/\r/g, "\n").replace(/\n+$/g, "")
}

export function shouldInsertMarkdownAsBlock(markdown: string): boolean {
  const trimmed = markdown.trimStart()
  return (
    markdown.includes("\n") ||
    /^#{1,6}\s/.test(trimmed) ||
    /^(-|\*|\+)\s+/.test(trimmed) ||
    /^\d+\.\s+/.test(trimmed) ||
    /^>/.test(trimmed) ||
    /^```/.test(trimmed) ||
    /^\|/.test(trimmed)
  )
}

/** Editor HTML for AI output: inline when it is a single plain line, blocks otherwise. */
export function markdownToInsertableHtml(markdown: string): string {
  const normalized = normalizeInsertedMarkdown(markdown)
  return shouldInsertMarkdownAsBlock(normalized)
    ? markdownToTiptapHtml(normalized)
    : markdownToTiptapHtmlInline(normalized)
}

export function parseResponseIntoCellValues(
  response: string,
  expectedCount: number,
  fallback: string[]
): string[] {
  const splitPattern = response.includes("|") && !response.includes("\n|") ? "|" : /\n/
  const values = response
    .split(splitPattern)
    .map((value) => value.trim())
    .filter((value) => value.length > 0)
  while (values.length < expectedCount) values.push(fallback[values.length] ?? "")
  return values.slice(0, expectedCount)
}

export function applyCellReplacements(
  editor: Editor,
  cellRanges: EditRange[],
  cellValues: string[]
): EditRange[] {
  const { state, view } = editor
  let tr = state.tr
  const sorted = [...cellRanges].sort((a, b) => b.from - a.from)
  for (const [idx, range] of sorted.entries()) {
    const valIdx = cellRanges.length - 1 - idx
    const html = markdownToTiptapHtmlInline(cellValues[valIdx] ?? "")
    const $from = tr.doc.resolve(tr.mapping.map(range.from))
    const depth = findCellDepth($from)
    if (depth === -1) continue
    const $to = tr.doc.resolve(tr.mapping.map(range.to))
    tr = tr.replaceRange($from.start(depth), $to.end(depth), parseHtmlToSlice(state.schema, html))
  }
  view.dispatch(tr)
  return cellRanges.map((r) => ({ from: tr.mapping.map(r.from), to: tr.mapping.map(r.to) }))
}

/**
 * Builds the transaction that replaces `range` with `insertedHtml`, and the
 * range the new content occupies (the suggestion highlight).
 *
 * Discarding a suggestion puts `originalHtml` back over that range, so the edit
 * is only returned when that discard would restore the document exactly. A
 * range whose replacement would reshape content around it (a selection that
 * runs from a paragraph into a list, across table cells, …) yields null and
 * the document is left alone.
 */
export function buildRangeReplacement(
  state: EditorState,
  range: EditRange,
  { originalHtml, insertedHtml }: { originalHtml: string; insertedHtml: string }
): { tr: Transaction; range: EditRange } | null {
  const { schema } = state
  if (selectionCrossesTableCells(state.doc, range.from, range.to)) return null

  const tr = state.tr.replaceRange(range.from, range.to, parseHtmlToSlice(schema, insertedHtml))
  const from = tr.mapping.map(range.from, -1)
  const to = tr.mapping.map(range.to, 1)
  if (from >= to) return null

  const discarded = new Transform(tr.doc).replaceRange(
    from,
    to,
    parseHtmlToSlice(schema, originalHtml)
  )
  if (!discarded.doc.eq(state.doc)) return null

  return { tr, range: { from, to } }
}
